(function () {
  'use strict';

  /**
   * <cart-checkout-price>
   *
   * Wraps the checkout button's price. The current-price span inside it
   * (data-cart-total-price) is owned by theme.js — it does an
   * un-guarded this.cartTotalPrice.innerHTML = ... on every cart update
   * (see the connectedCallback/refresh in theme.js's cart-element class),
   * so it's left completely alone here, and this element's own markup
   * must never be placed inside it (theme.js's innerHTML write would
   * silently wipe it out on the very next cart change).
   *
   * The compare-at (struck-through) price is a sibling instead, updated
   * independently on theme:cart:added/refresh/update — same fetch
   * pattern as assets/bundle-offers.js and assets/cart-progress-tiers.js.
   */
  customElements.get('cart-checkout-price') ||
    customElements.define(
      'cart-checkout-price',
      class extends HTMLElement {
        connectedCallback() {
          this.currencyEnable = window.theme?.settings?.currency_code_enable ?? false;

          this.boundRefresh = this.refresh.bind(this);
          ['theme:cart:added', 'theme:cart:refresh', 'theme:cart:update'].forEach((eventName) => {
            document.addEventListener(eventName, this.boundRefresh);
          });
        }

        disconnectedCallback() {
          ['theme:cart:added', 'theme:cart:refresh', 'theme:cart:update'].forEach((eventName) => {
            document.removeEventListener(eventName, this.boundRefresh);
          });
        }

        refresh() {
          // Looked up fresh on every call rather than cached once in
          // connectedCallback: this element is cloned out of a
          // <template> (snippets/cart-drawer.liquid), and confirmed live
          // that connectedCallback can fire before that clone's full
          // subtree is actually attached — a querySelector done there
          // came back null even though the child was present moments
          // later, silently breaking every future refresh() (which
          // bailed out on the cached null forever, never picking the
          // element back up). Re-querying here has no such race.
          this.compareEl = this.querySelector('[data-cart-checkout-compare-price]');
          if (!this.compareEl) return;

          // cart.js (the Ajax Cart API) has no compare_at_price field on
          // line items at all, so the pre-markdown total this element
          // needs can't be recomputed from it client-side. Fetching the
          // api-cart-items section instead (theme.js's own cart-update
          // fetch already uses this same section_id, see assets/
          // theme.js) re-runs the real Liquid computation — sections/
          // api-cart-items.liquid mirrors checkout_compare_total and
          // exposes it (plus the current subtotal) as data attributes on
          // a [data-checkout-price-data] element specifically for this.
          fetch(window.theme.routes.cart_url + '?section_id=api-cart-items')
            .then((response) => response.text())
            .then((html) => this.update(html))
            .catch((error) => console.error('cart-checkout-price:', error));
        }

        update(html) {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const data = doc.querySelector('[data-checkout-price-data]');
          if (!data) return;

          const subtotal = parseInt(data.dataset.subtotal, 10) || 0;
          const compareTotal = parseInt(data.dataset.checkoutCompareTotal, 10) || 0;
          const onSale = compareTotal > subtotal;
          this.compareEl.hidden = !onSale;

          if (onSale && window.theme?.formatMoney) {
            const format = this.currencyEnable ? window.theme.moneyWithCurrencyFormat : window.theme.moneyFormat;
            this.compareEl.textContent = window.theme.formatMoney(compareTotal, format);
          }
        }
      }
    );
})();
